/**
 * Payment Statuses Hook for ArendaPro+
 * 
 * Provides CRUD operations for payment statuses using the new domain-specific table.
 * Replaces the generic tags system for payment status management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  PaymentStatus, 
  PaymentStatusDb, 
  PaymentStatusUpdate,
  DomainStatusOption,
  DomainStatusFilters
} from '@/types/domain-statuses';
import {
  paymentStatusDbToClient,
  paymentStatusClientToDbInsert,
  paymentStatusUpdateToDb,
  statusesToOptions,
  validateStatusName,
  validateColorCode,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';
import { useLogger } from '@/utils/logging/logger';

// =============================================================================
// QUERY KEYS
// =============================================================================

const PAYMENT_STATUSES_QUERY_KEY = 'paymentStatuses';

export const paymentStatusesKeys = {
  all: [PAYMENT_STATUSES_QUERY_KEY] as const,
  lists: () => [...paymentStatusesKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...paymentStatusesKeys.lists(), filters] as const,
  details: () => [...paymentStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentStatusesKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export type UsePaymentStatusesOptions = {
  filters?: DomainStatusFilters;
  enabled?: boolean;
};

type UsePaymentStatusesResult = {
  paymentStatuses: PaymentStatus[];
  options: DomainStatusOption[];
  defaultStatus: PaymentStatus | undefined;
  paidStatus: PaymentStatus | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => unknown;
  create: (newStatus: Omit<PaymentStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void;
  update: (args: { id: string; updates: PaymentStatusUpdate }) => void;
  delete: (id: string) => void;
  reorder: (items: Array<{ id: string; sortOrder: number }>) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isReordering: boolean;
  createError: unknown;
  updateError: unknown;
  deleteError: unknown;
  reorderError: unknown;
};

export function usePaymentStatuses(options: UsePaymentStatusesOptions = {}): UsePaymentStatusesResult {
  const logger = useLogger('usePaymentStatuses');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true } = options;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: paymentStatuses = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: paymentStatusesKeys.list(filters),
    queryFn: async (): Promise<PaymentStatus[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('payment_statuses')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching payment statuses', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to fetch payment statuses: ${msg}`);
      }

      return (data as PaymentStatusDb[]).map(paymentStatusDbToClient);
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newStatus: Omit<PaymentStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<PaymentStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newStatus);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название статуса оплаты должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates
      const existingStatus = paymentStatuses.find(
        status => status.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingStatus) {
        throw new Error('Статус оплаты с таким названием уже существует');
      }

      const insertData = paymentStatusClientToDbInsert({
        ...sanitized,
        userId: user.id,
        color: sanitized.color || '#f59e0b', // default color
      } as Omit<PaymentStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string });

      const { data, error } = await supabaseWorker
        .from('payment_statuses')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        logger.error('Error creating payment status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to create payment status: ${msg}`);
      }

      return paymentStatusDbToClient(data as PaymentStatusDb);
    },
    onSuccess: (newStatus) => {
      // Invalidate and refetch payment statuses
      queryClient.invalidateQueries({ queryKey: paymentStatusesKeys.all });
      toast.success(`Статус оплаты "${newStatus.name}" создан`);
    },
    onError: (error: Error) => {
      logger.error('Error creating payment status', error);
      toast.error(error.message || 'Ошибка при создании статуса оплаты');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PaymentStatusUpdate }): Promise<PaymentStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название статуса оплаты должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates (excluding current status)
      if (sanitized.name) {
        const existingStatus = paymentStatuses.find(
          status => status.id !== id && status.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingStatus) {
          throw new Error('Статус оплаты с таким названием уже существует');
        }
      }

      const updateData = paymentStatusUpdateToDb(sanitized);

      const { data, error } = await supabaseWorker
        .from('payment_statuses')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        logger.error('Error updating payment status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to update payment status: ${msg}`);
      }

      if (!data) {
        throw new Error('Payment status not found or access denied');
      }

      return paymentStatusDbToClient(data as PaymentStatusDb);
    },
    onSuccess: (updatedStatus) => {
      // Invalidate and refetch payment statuses
      queryClient.invalidateQueries({ queryKey: paymentStatusesKeys.all });
      toast.success(`Статус оплаты "${updatedStatus.name}" обновлен`);
    },
    onError: (error: Error) => {
      logger.error('Error updating payment status', error);
      toast.error(error.message || 'Ошибка при обновлении статуса оплаты');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('payment_statuses')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        logger.error('Error deleting payment status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to delete payment status: ${msg}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted status name for toast
      const deletedStatus = paymentStatuses.find(status => status.id === deletedId);
      
      // Invalidate and refetch payment statuses
      queryClient.invalidateQueries({ queryKey: paymentStatusesKeys.all });
      toast.success(`Статус оплаты "${deletedStatus?.name || ''}" удален`);
    },
    onError: (error: Error) => {
      console.error('Error deleting payment status:', error);
      toast.error(error.message || 'Ошибка при удалении статуса оплаты');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('payment_statuses')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering payment statuses:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentStatusesKeys.all });
      toast.success('Порядок обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering payment statuses:', error);
      toast.error(error.message || 'Ошибка при изменении порядка');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const statusOptions: DomainStatusOption[] = statusesToOptions(paymentStatuses);
  const defaultStatus = paymentStatuses.find(status => 
    status.name.toLowerCase() === 'не оплачено' || 
    status.name.toLowerCase() === 'unpaid'
  );

  const paidStatus = paymentStatuses.find(status => 
    status.name.toLowerCase() === 'оплачено' || 
    status.name.toLowerCase() === 'paid'
  );

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    paymentStatuses,
    options: statusOptions,
    defaultStatus,
    paidStatus,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting payment status options for selectors
 */
export function usePaymentStatusOptions(filters?: DomainStatusFilters): {
  options: DomainStatusOption[];
  isLoading: boolean;
  error: unknown;
} {
  const { options, isLoading, error } = usePaymentStatuses({ filters });
  
  return {
    options,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single payment status by ID
 */
export function usePaymentStatus(id: string | undefined): ReturnType<typeof useQuery<PaymentStatus | null, Error>> {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: paymentStatusesKeys.detail(id || ''),
    queryFn: async (): Promise<PaymentStatus | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('payment_statuses')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === 'PGRST116') {
          return null; // Not found
        }
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to fetch payment status: ${msg}`);
      }

      return paymentStatusDbToClient(data as PaymentStatusDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
